import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableRow, Paper, Tooltip, AlertColor, IconButton, Typography } from '@mui/material';
import { getSessionLeaderboard, getSessionScoreCards, getScenarioHeaders, downloadScenarioResultsToCSV, getSessionWithSessionId, getGroup } from 'src/Services/BorgAPIClient';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { CampaignType, ScenarioType, ScoreCardType } from 'src/Constants/BorgEnums';
import { GroupCompetition, ScenarioHeader, UserMinimal } from 'src/Constants/Types';
import { removeIllegalChars } from 'src/Util/Util';
import { Link } from '@mui/material';
import { 
  colorGradientBackground1_Green, 
  colorGradientBackground2, 
  colorGradientBackground3, 
  colorGradientBackground4, 
  colorGradientBackground5, 
  colorGradientBackground6_Red 
} from 'src/GlobalStyles/MuiTheme';
import { LoggedInUserContext } from './AdminPageContainer';
import { getCourseId } from 'src/Constants/ScenarioData';

const MAX_SCENARIO_NAME_LENGTH = 20;

//This is a combination of both Score Card (for Learning) and Leaderboard (For scored)
interface ScoreCardAndLeaderboard {
  id: string;
  userId: string;
  courseId: string;
  campaignId: string | null;
  campaignName: string | null;
  scenarioId: string;
  scenarioName: string | null;
  userName: string;
  time: string;
  totalScore: number | null;
  maxTotalScore: number | null;
  passed: boolean;
  rank: number | null;
  maxQuestionsScore: number | null;
  questionsScore: number | null;
  sessionId: string;
  scoreCardType: ScoreCardType;
  questionScorecards: [] | null
}

type LetterGradeTypographyProps = {
  grade: React.ReactNode;
  cellBackgroundColor: string;
};
const LetterGradeTypography = ({grade, cellBackgroundColor} : LetterGradeTypographyProps) => {
  return (
    <Typography 
      fontWeight='bold' 
      fontSize={18} component="span" 
      style={{ backgroundColor: cellBackgroundColor, minWidth: '22px', minHeight: '24px', borderRadius: '4px' }}
    >
      {grade}
    </Typography>
  );
}

const computeTotalScore = (score: ScoreCardAndLeaderboard) => {
  const totalScore = (score.totalScore ?? 0) + (score.questionsScore ?? 0);
  return Math.floor(totalScore);
}

const computeMaxTotalScore = (score: ScoreCardAndLeaderboard) => {
  const maxTotalScore = (score.maxTotalScore ?? 0) + (score.maxQuestionsScore ?? 0);
  return Math.floor(maxTotalScore);
}

// Component for Compete Score Display
const CompeteScoreDisplay = ({ score }: { score: ScoreCardAndLeaderboard | null }) => {
  if (score) {

    const totalScore = computeTotalScore(score);
    const maxTotalScore = computeMaxTotalScore(score);

    const percentage = totalScore / maxTotalScore;
    const roundedPercentage = Math.round(percentage * 100);
    let grade = "";
    let cellBackgroundColor = 'inherit';

    if (roundedPercentage >= 95) {
      grade = "A+";
      cellBackgroundColor = colorGradientBackground1_Green; // Dark green
    } else if (roundedPercentage >= 90) {
      grade = "A";
      cellBackgroundColor = colorGradientBackground2; // Slightly lighter green
    } else if (roundedPercentage >= 80) {
      grade = "B";
      cellBackgroundColor = colorGradientBackground3; // Lighter green
    } else if (roundedPercentage >= 70) {
      grade = "C";
      cellBackgroundColor = colorGradientBackground4; // Dark yellow
    } else if (roundedPercentage >= 60) {
      grade = "D";
      cellBackgroundColor = colorGradientBackground5; // Light brown
    } else {
      grade = "F";
      cellBackgroundColor = colorGradientBackground6_Red; // Dark red
    }

    // check scoreCardType and questionScorecards to determine if the scorecards is a quiz scorecord or not
    if (score.scoreCardType !== ScoreCardType.Quiz && (!score.questionScorecards || score.questionScorecards.length === 0)) {
      return (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <LetterGradeTypography grade={grade} cellBackgroundColor={cellBackgroundColor}/>
          <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'right', paddingLeft: '3px' }}>
            <div>{roundedPercentage}%</div>
          </div>
        </div>
      );
    }

    // ELSE calculate the number of quiz questions answered correctly
    let totalNumberOfQuestions = 0;
    let totalQuestionsAnsweredCorrectly = 0;

    if (score.questionScorecards) {
      score.questionScorecards.forEach((questionScorecard: any) => {
        if (questionScorecard.maxScore !== 0) {
          totalNumberOfQuestions++;
          if (questionScorecard.maxScore === questionScorecard.score) {
            totalQuestionsAnsweredCorrectly++;
          }
        }
      });
    }

    return (
      // TODO - Make this a reusable style to prevent inconsistencies with the cell style defined above
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <LetterGradeTypography grade={grade} cellBackgroundColor={cellBackgroundColor} />
        <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'right' }}>
          <div>{roundedPercentage}%</div>
          <div>{totalQuestionsAnsweredCorrectly} / {totalNumberOfQuestions}</div>
        </div>
      </div>
    );
  }
  else {
    return (
      <div>
        -
      </div>
    )
  }
};

const GradebookTable = ({ sessionStudents, session, onNotification }: {
  sessionStudents: UserMinimal[] | null,
  session: GroupCompetition | undefined
  onNotification: (message: string, notificationType?: AlertColor) => void
}) => {

  const [scores, setScores] = useState<{ [userId: string]: { [scenarioId: string]: ScoreCardAndLeaderboard } }>({});
  const [scenarioList, setScenarioList] = useState<ScenarioHeader[]>([]);

  const selectedPage = useContext(LoggedInUserContext)?.selectedPage;   // Selected page (used for identifying if the user is on the instructor panel when handling quiz click)
  const setQuizContext = useContext(LoggedInUserContext)?.setQuizContext;
  
  const navigate = useNavigate();

  const fetchScenarioScores = useCallback(async () => {
    if (session && session.id && session.groupId) {
      
      const scoreMap: { [userId: string]: { [scenarioId: string]: ScoreCardAndLeaderboard } } = {};

      let scenarioListResult: ScenarioHeader[] = [];
      let scenarioScores: ScoreCardAndLeaderboard[] = [];
      let scenarioScoresResult;

      let ownerGroup = null;
      let courseId = "";
      
      // get the session's group's owner organization
      try {
        const res = await getGroup(session.groupId);
        ownerGroup = res.data;
      } catch (error) {
        const msg = "Failed to get session owner group";
        console.log(msg, error);
        onNotification(msg, "error");
      }

      if(ownerGroup && ownerGroup.ownerOrganizationId) {
        courseId = getCourseId(ownerGroup.ownerOrganizationId);

        //Obtain list of scenario headers (course, campaign, and scenario) for each scenario in the course
        try {
          const res = await getScenarioHeaders(courseId);
          scenarioListResult = res.data;
          console.log(`Found ${scenarioListResult.length} scenarios for group ${session.groupId} session ${session.id} course ${courseId}`);
          setScenarioList(scenarioListResult);
        } catch (error) {
          const msg = "Failed to get scenarios";
          console.log(msg, error);
          onNotification(msg, "error");
        }
      }

      //iterate through scenarioList and execute getSessionLeaderboard. 
      for (let i = 0; i < scenarioListResult.length; i++) {

        //Handle use of getScores for learning score retrieval
        if (scenarioListResult[i].campaignType === CampaignType.Learn || scenarioListResult[i].scenarioType === ScenarioType.Quiz) {

          //Obtain score for each user for each learn scenario
          try {
            scenarioScoresResult = await getSessionScoreCards(session.id, courseId, scenarioListResult[i].campaignId, scenarioListResult[i].scenarioId);
            //Put all users scores into the scores array
            scenarioScores.push(...scenarioScoresResult.data as ScoreCardAndLeaderboard[]);
          } catch (error) {
            const msg = "Failed to get session scorecards";
            console.log(msg, error);
            onNotification(msg, "error");
          }

          if (scenarioScores.length === 0) {
            const msg = "No learn session scorecards found";
            console.log(msg);
          }
        }

        //Handle use of getSessionLeaderBoard for non learning score retrieval
        else {
          try {
            scenarioScoresResult = await getSessionLeaderboard(session.id, courseId, scenarioListResult[i].campaignId, scenarioListResult[i].scenarioId);
          } catch (error) {
            const msg = "Failed to get session leaderboard";
            console.log(msg, error);
            onNotification(msg, "error");
          }

          if (scenarioScoresResult?.data?.topScores) {
            scenarioScores = scenarioScoresResult.data.topScores as ScoreCardAndLeaderboard[];  //Scores are nested in a TopScores object    
          } else {
            const msg = "No compete session leaderboard found";
            console.log(msg);
          }
        }
        
        //Fill the scoreMap with score data obtain from scenario 
        scenarioScores.forEach(score => {
          if (!scoreMap[score.userId]) {
            scoreMap[score.userId] = {};
          }
          // scorecards are ordered by TotalScore, so only keep the first (highest scoring) instance of each
          if (!scoreMap[score.userId][score.scenarioId])
            scoreMap[score.userId][score.scenarioId] = score;
        });
      }

      setScores(scoreMap);
    }
  }, [session]);

  useEffect(() => {
    fetchScenarioScores();
  }, [fetchScenarioScores]);

  const getTableFormattedScenarioName = (scenario: ScenarioHeader) => {
    let jsx;
    const trimmedScenarioName = (scenario.scenarioShortTitle ? scenario.scenarioShortTitle : scenario.scenarioTitle).trim();

    if (trimmedScenarioName.length > MAX_SCENARIO_NAME_LENGTH) {
      // split into two rows, end with ellipsis
      jsx = (
        <div style={{
          width: 'auto',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }}>
          {trimmedScenarioName.substring(0, MAX_SCENARIO_NAME_LENGTH / 2)}<br />{trimmedScenarioName.substring(MAX_SCENARIO_NAME_LENGTH / 2, MAX_SCENARIO_NAME_LENGTH)}...
        </div>
      );
    } else {
      // use as is           
      jsx = <div style={{
        width: 'auto',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        wordWrap: 'break-word'
      }}>
        {trimmedScenarioName}
      </div>
    }

    // Wrap the jsx in a link if the scenario type is Quiz
    if (scenario.scenarioType === ScenarioType.Quiz) {
      // If instructor panel, remove the open in new tab functionality and icon
      if (selectedPage === "instructor") {
        return (
          <Link
          href="#"
          rel="noopener noreferrer"
          onClick={() => handleQuizClick(scenario)}
          style={{ 
            color: 'inherit', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            textDecoration: 'underline' 
          }}
            >
          {jsx}
            </Link>
        );
      } else {
        return (
          <Link
        href="#"
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        onClick={() => handleQuizClick(scenario)}
        style={{ color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
        {jsx} <OpenInNewIcon sx={{ fontSize: '1.25em', marginLeft: '2px' }} />
          </Link>
        );
      }
    }

    return jsx;
  }

  const getCellValue = (score: ScoreCardAndLeaderboard, scenario: ScenarioHeader) => {
    if (!score) return '-';
    if (scenario.scenarioType === ScenarioType.Quiz || scenario.campaignType === CampaignType.Compete) {
      // if scored quiz or compete scenario, then return the score and grade
      return <CompeteScoreDisplay score={score} />;
    } else if (scenario.scenarioId) {
      // else if a lession (pass/fail), return a check if passed and an X if failed
      if (score.passed) {
        return <CheckCircleIcon color="success" />;
      } else {
        return <CancelIcon color="error" />;
      }
    } else {
      // otherwise, the scenario type is not recognized, just return the score
      return computeTotalScore(score);
    }
  }

  const handleDownloadResultsClick = async (sessionId: string, scenarioId: string, scenarioName: string | undefined) => {
    let filename = scenarioName ? `Quiz//Results - ${scenarioName}.csv` : "Quiz Results.csv";
    filename = removeIllegalChars(filename);
    try {
      await downloadScenarioResultsToCSV(sessionId, scenarioId, filename);
    }
    catch (error) {
      const msg = "Failed to download results";
      console.error(msg, error);
      onNotification(msg, "error");
    }
  }

  const handleQuizClick = (scenario: ScenarioHeader) => {
    // If instructor panel use breadcrumb drill down and remove open in new tab icon

    if (selectedPage === "instructor") {
      // set the quiz context so the proper quiz button will display in the instructor panel
      if (setQuizContext) {
        setQuizContext(scenario.scenarioId, session?.id ?? '');
      } else {
        console.error("setQuizContext is undefined");
      }

      let url;
      // encode the name and short title to avoid issues with special characters
      const encodedName = encodeURIComponent(session?.name ?? '');
      const encodedShortTitle = encodeURIComponent(scenario.scenarioShortTitle ?? '');

      url = `/instructor/gradebook/${encodedName}/${encodedShortTitle}`;

      navigate(url);
    }
    else { // If not on instructor page, open in new tab and include new tab icon 
      // Store the scenarioId and sessionId in local storage (used for new tab)
      localStorage.setItem('quizId', scenario.scenarioId);
      localStorage.setItem('sessionId', session?.id ?? '');

      let url;
      // encode the name and short title to avoid issues with special characters
      const encodedName = encodeURIComponent(session?.name ?? '');
      const encodedShortTitle = encodeURIComponent(scenario.scenarioShortTitle ?? '');

      url = `/gradebook/${encodedName}/${encodedShortTitle}`;

      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const getScenarioNameIfDetailedResultsExist = (scenarioId: string) => {

    let hasScoreCard = false;
    let scenarioName: string | undefined;

    if (session?.id && scenarioId) {
      for (const userId in scores) {

        // check if valid detailed results exists
        const scoreCard = scores[userId][scenarioId];
        hasScoreCard = !!(scoreCard && scoreCard.questionScorecards && scoreCard.questionScorecards.length > 0);

        if (!hasScoreCard) continue;

        // if detailed results exist, return a suitable scenario name
        const scenarioHeader = scenarioList.find(p => p.scenarioId === scenarioId);
        scenarioName = scenarioHeader?.scenarioShortTitle ? scenarioHeader.scenarioShortTitle : scoreCard.scenarioName?.substring(0, 10);
        break;
      }
    }

    return scenarioName;
  }

  const renderCsvDownloadButton = (scenarioId: string) => {
    // check if the scenario has any scorecards
    let scenarioName: string | undefined;
    if (session?.id && scenarioId && (scenarioName = getScenarioNameIfDetailedResultsExist(scenarioId))) {
      // show download button if there is at least one scorecard
      return (
        <Tooltip title="Click to download detailed results" arrow>
          <IconButton onClick={() => handleDownloadResultsClick(session.id, scenarioId, scenarioName)}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      );
    } else {
      // hide download button if no scorecards
      return null;
    }
  }

  return (
    <Paper elevation={3} style={{ width: 'fit-content', border: '1px solid black', }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {/* Campaign Name Row */}
            <TableCell style={{ border: '1px solid black', }}></TableCell>
            {(() => {
              const campaignGroups: { [campaignName: string]: ScenarioHeader[] } = {};

              // Group scenarios by campaign name
              scenarioList.forEach(scenario => {
                const campaignName = scenario.campaignTitle || '';
                if (!campaignGroups[campaignName]) {
                  campaignGroups[campaignName] = [];
                }
                campaignGroups[campaignName].push(scenario);
              });

              return Object.entries(campaignGroups).map(([campaignName, scenarios]) => (
                <TableCell
                  key={campaignName}
                  colSpan={scenarios.length}
                  style={{
                    textAlign: 'center',
                    fontWeight: 'bold',
                    border: '1px solid black',
                  }}
                >
                  {campaignName}
                </TableCell>
              ));
            })()}
          </TableRow>

          {/* Scenario Names Row */}
          <TableRow>
            <TableCell style={{ border: '1px solid black', minWidth: "120px" }}>User Name</TableCell>
            {scenarioList.map(scenario => (
                <TableCell key={scenario.scenarioId} style={{ border: '1px solid black', textAlign: 'center', padding: '8px', minWidth: "96px"}}>
                <Tooltip title={scenario.scenarioTitle} arrow>
                  {getTableFormattedScenarioName(scenario)}
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>

          {/* Scenario results download row */}
          <TableRow>
            <TableCell style={{ border: '1px solid black', minWidth: "120px" }} />
            {scenarioList.map(scenario => (
              <TableCell key={scenario.scenarioId} style={{ border: '1px solid black', textAlign: 'center', paddingLeft: 8, paddingRight: 8, paddingTop: 8, paddingBottom: 8 }}>
                {renderCsvDownloadButton(scenario.scenarioId)}
              </TableCell>
            ))}
          </TableRow>

        </TableHead>
        <TableBody>
          {sessionStudents?.map(user => (
            <TableRow key={user.id}>
              <TableCell style={{ border: '1px solid black', }}>{user.username}</TableCell>
              {scenarioList.map(scenario => {
                const scenarioId = scenario.scenarioId === "" ? "0" : scenario.scenarioId //This accounts for the null scenario ID used to find total scores (null index must be replaced)
                const score = scores[user.id]?.[scenarioId];
                return (
                  //Scores, grades, and pass
                  <TableCell
                    key={scenario.scenarioId}
                    style={{ textAlign: 'center', verticalAlign: 'middle', border: '1px solid black', padding: "4px 8px" }}
                  >
                    {getCellValue(score, scenario)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default GradebookTable;